import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, Calendar, Mail, Power } from "lucide-react";
import api from "@/lib/api";

/**
 * Admin-only settings for pre-validation of proposals.
 *
 * When enabled (optionally scoped to a date range), every attempt to
 * send a proposal to the client is intercepted and instead routed to
 * the validator (default: rui.pinto@caring.pt) who can approve, edit
 * key values or request changes via the public review page.
 */
export default function ProposalPrevalidationSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/proposal-prevalidation-config");
      setCfg(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a carregar configuração.");
      setCfg({});
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/proposal-prevalidation-config", {
        enabled: !!cfg.enabled,
        active_from: cfg.active_from || null,
        active_until: cfg.active_until || null,
        validator_email: (cfg.validator_email || "").trim() || "rui.pinto@caring.pt",
      });
      setCfg(r.data);
      toast.success(
        r.data.enforced_now
          ? "Guardado — pré-validação ACTIVA neste momento."
          : "Guardado.",
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (cfg === null) {
    return (
      <div className="p-8 lg:p-12 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto space-y-6" data-testid="prevalidation-settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" /> Definições
        </div>
        <h1 className="font-display text-3xl lg:text-4xl tracking-tight">
          Validação prévia de propostas
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Quando activo, cada tentativa de enviar uma proposta ao cliente
          fica em espera até que o validador aprove, edite ou peça
          alterações. O validador recebe email com link seguro para decidir.
        </p>
      </div>

      {/* Status live */}
      <div
        className={`rounded-md border p-4 flex items-center gap-3 ${
          cfg.enforced_now
            ? "bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/30 text-[hsl(var(--success))]"
            : "bg-muted/40 border-border text-muted-foreground"
        }`}
        data-testid="prevalidation-live-status"
      >
        <Power className="w-4 h-4" />
        <div className="text-sm">
          {cfg.enforced_now
            ? "Neste momento a pré-validação está a intercetar envios de propostas."
            : "Neste momento a pré-validação NÃO está a intercetar envios (feature desligada OU fora do intervalo de datas)."}
        </div>
      </div>

      {/* Toggle + fields */}
      <div className="rounded-md border border-border bg-card p-5 space-y-5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => patch("enabled", e.target.checked)}
            className="mt-1 w-4 h-4 accent-primary"
            data-testid="prevalidation-toggle-enabled"
          />
          <div className="flex-1">
            <div className="font-semibold text-sm">Activar validação prévia</div>
            <div className="text-[13px] text-muted-foreground mt-0.5">
              Enquanto ligada e dentro do intervalo de datas, nenhuma
              proposta sai directamente ao cliente sem aprovação.
            </div>
          </div>
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5 mb-1.5">
              <Calendar className="w-3 h-3" /> Vigente a partir de
            </label>
            <input
              type="date"
              value={cfg.active_from || ""}
              onChange={(e) => patch("active_from", e.target.value || null)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="prevalidation-active-from"
            />
            <div className="text-[11px] text-muted-foreground mt-1">Vazio = sem limite inicial.</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5 mb-1.5">
              <Calendar className="w-3 h-3" /> Vigente até
            </label>
            <input
              type="date"
              value={cfg.active_until || ""}
              onChange={(e) => patch("active_until", e.target.value || null)}
              className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              data-testid="prevalidation-active-until"
            />
            <div className="text-[11px] text-muted-foreground mt-1">Vazio = sem limite final.</div>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold flex items-center gap-1.5 mb-1.5">
            <Mail className="w-3 h-3" /> Email do validador
          </label>
          <input
            type="email"
            value={cfg.validator_email || ""}
            onChange={(e) => patch("validator_email", e.target.value)}
            placeholder="rui.pinto@caring.pt"
            className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
            data-testid="prevalidation-validator-email"
          />
          <div className="text-[11px] text-muted-foreground mt-1">
            Endereço que recebe o email de validação com o link seguro. Só um destinatário.
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
          <div className="text-[11px] text-muted-foreground">
            {cfg.updated_at ? `Última alteração: ${new Date(cfg.updated_at).toLocaleString("pt-PT")} por ${cfg.updated_by_name || "—"}` : "Nunca guardado."}
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold disabled:opacity-50"
            data-testid="prevalidation-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Info box */}
      <div className="rounded-md border border-border bg-[hsl(var(--info-bg))]/40 p-4 text-sm text-foreground">
        <div className="font-semibold text-[13px] mb-1">Como funciona</div>
        <ol className="text-[13px] space-y-1 ml-4 list-decimal text-muted-foreground">
          <li>Consultor clica em <b>Enviar Proposta</b> no lead.</li>
          <li>Se activo, o sistema cria pedido pendente e envia email ao validador.</li>
          <li>Validador abre o link, revê PDF, e escolhe: <b>Aprovar</b> (com edições opcionais) OU <b>Pedir alterações</b> (motivo obrigatório).</li>
          <li>Consultor recebe notificação com decisão. Se aprovado, clica de novo em <b>Enviar Proposta</b> — dessa vez segue para o cliente.</li>
          <li>Se pedidas alterações, corrige e reenvia para nova validação.</li>
        </ol>
      </div>
    </div>
  );
}
